import { Alert, Button, Card, Input, Space, Typography } from "antd";
import type { AxiosResponse } from "axios";
import { useEffect, useState } from "react";

import request from "@/api";
import type { CustomAxiosRequestConfig } from "@/api";
import {
    DateRangePicker as ZhDateRangePicker,
    Pagination as ZhPagination,
    Table as ZhTable,
    type TableColumnProps
} from "zhihao-ui-react";
import { dateTimeFormat } from "@/utils";
import { useUserStore } from "@/views/stores";
import { ZoneRechargePage, type ZoneRechargePageResult, type ZoneRechargeQuery } from "./ZoneRechargePage";
import { ZoneConsumptionPage, type ZoneConsumptionPageResult, type ZoneConsumptionQuery } from "./ZoneConsumptionPage";

export interface ZoneAccountRow {
    account?: { id?: string | number; createdTime?: string };
    specialZoneName?: string;
    realName?: string;
    customerCode?: string;
    mobile?: string;
    institutionName?: string;
    balanceAmount?: number;
}

export interface ZoneAccountPageResult {
    list?: ZoneAccountRow[];
    total?: number;
}

export interface ZoneAccountQuery {
    page: number;
    size: number;
    accountId?: string;
    keyword?: string;
    startDate?: string;
    endDate?: string;
    sortKey?: string;
    sortOrder?: "asc" | "desc";
}

export interface ZoneAccountPageProps {
    exportAccounts?: (query: ZoneAccountQuery) => Promise<void>;
    loadAccounts?: (query: ZoneAccountQuery) => Promise<ZoneAccountPageResult>;
    loadRecharges?: (query: ZoneRechargeQuery) => Promise<ZoneRechargePageResult>;
    loadConsumptions?: (query: ZoneConsumptionQuery) => Promise<ZoneConsumptionPageResult>;
    permissions?: string[];
}

const defaultQuery: ZoneAccountQuery = { page: 1, size: 10 };

/** 复用 Vue 专区余额管理账户查询接口。 */
export async function loadZoneAccounts(query: ZoneAccountQuery): Promise<ZoneAccountPageResult> {
    const response = await request.service.get<ZoneAccountPageResult>("/admin/special-zone-account/paging", {
        params: query
    });
    return response.data;
}

/** 从 Vue 导出响应头中读取文件名，缺失或异常时使用稳定的默认文件名。 */
function getZoneAccountExportFilename(contentDisposition?: string): string {
    const fallback = "专区账户列表.xlsx";
    if (!contentDisposition) return fallback;

    const encodedFilename = contentDisposition.match(/filename\*=(?:UTF-8'')?([^;]+)/i)?.[1]?.trim();
    const filename = encodedFilename || contentDisposition.match(/filename=([^;]+)/i)?.[1]?.trim();
    if (!filename) return fallback;

    const normalizedFilename = filename.replace(/^['"]|['"]$/g, "");
    try {
        return decodeURIComponent(normalizedFilename) || fallback;
    } catch {
        return normalizedFilename || fallback;
    }
}

/** 将 Vue 文件流转换为浏览器下载，并在下载触发后清理临时节点和 Object URL。 */
function downloadZoneAccountBlob(blob: Blob, filename: string): void {
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.setAttribute("download", filename);
    document.body.appendChild(link);

    try {
        link.click();
    } finally {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(objectUrl);
    }
}

/** 沿用 Vue 专区账户导出接口，提交当前筛选条件并接收原始 Blob 响应。 */
export async function exportZoneAccounts(query: ZoneAccountQuery): Promise<void> {
    const response = await request.service.get<Blob, AxiosResponse<Blob>>("/admin/special-zone-account/export", {
        params: query,
        responseType: "blob",
        returnRawResponse: true
    } as CustomAxiosRequestConfig);
    const filename = getZoneAccountExportFilename(response.headers["content-disposition"]);
    downloadZoneAccountBlob(response.data, filename);
}

/** 将 Vue 账户列表中的主体信息转换为原页面显示文案。 */
function getCustomerLabel(row: ZoneAccountRow): string {
    return [row.realName, row.customerCode].filter(Boolean).join(" | ") || "--";
}

/** 专区余额管理首屏，保留 Vue 三 Tab 和账户查询列表契约。 */
export function ZoneAccountPage({
    exportAccounts = exportZoneAccounts,
    loadAccounts = loadZoneAccounts,
    loadRecharges,
    loadConsumptions,
    permissions
}: ZoneAccountPageProps) {
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const currentPermissions = permissions ?? storePermissions;
    const canManage = currentPermissions.includes("ROLE_ZONE_ACCOUNT_MANAGE");
    const [activeTab, setActiveTab] = useState<"account" | "recharge" | "consumption">("account");
    const [rows, setRows] = useState<ZoneAccountRow[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<ZoneAccountQuery>(defaultQuery);
    const [accountId, setAccountId] = useState("");
    const [keyword, setKeyword] = useState("");
    const [createdDateRange, setCreatedDateRange] = useState<[string | null, string | null]>([null, null]);
    const [rechargeAccountId, setRechargeAccountId] = useState<string>();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        let disposed = false;
        void loadAccounts(query)
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage(null);
            })
            .catch(() => {
                if (!disposed) setErrorMessage("专区账户加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadAccounts, query]);

    /** 保留 Vue 列表筛选参数名称和重置到第一页的行为。 */
    const search = () => {
        setQuery({
            ...defaultQuery,
            ...(accountId.trim() ? { accountId: accountId.trim() } : {}),
            ...(keyword.trim() ? { keyword: keyword.trim() } : {}),
            ...(createdDateRange[0] ? { startDate: createdDateRange[0] } : {}),
            ...(createdDateRange[1] ? { endDate: createdDateRange[1] } : {})
        });
    };

    /** 保留 Vue 开户时间的后端排序字段和升降序参数。 */
    const handleSortChange = ({ prop, order }: { prop?: string; order: "ascending" | "descending" | null }) => {
        if (prop !== "account.createdTime") return;
        setQuery(current => {
            const nextQuery: ZoneAccountQuery = { ...current, page: 1 };
            delete nextQuery.sortKey;
            delete nextQuery.sortOrder;
            if (order) {
                nextQuery.sortKey = "created_time";
                nextQuery.sortOrder = order === "ascending" ? "asc" : "desc";
            }
            return nextQuery;
        });
    };

    /** 只使用已提交的 Vue 查询条件导出，失败时保留可见的业务反馈。 */
    const handleExport = async () => {
        setExporting(true);
        setErrorMessage(null);
        try {
            await exportAccounts(query);
        } catch {
            setErrorMessage("专区账户导出失败");
        } finally {
            setExporting(false);
        }
    };

    const tab = (value: "account" | "recharge" | "consumption", label: string) => (
        <button
            aria-selected={activeTab === value}
            className="border-0 border-b-2 border-transparent bg-transparent px-0 pb-2 text-gray-500"
            role="tab"
            type="button"
            onClick={() => {
                // 对齐 Vue changeTab：离开充值明细后清空账户带入的筛选条件。
                if (value !== "recharge") setRechargeAccountId(undefined);
                setActiveTab(value);
            }}
        >
            {label}
        </button>
    );

    return (
        <section aria-label="zone-account-page" className="flex h-full min-h-full flex-col">
            <Card>
                <Typography.Title className="mb-4 mt-0" level={4}>
                    专区余额管理
                </Typography.Title>
                <div aria-label="专区余额管理页签" className="mb-4 flex gap-6 border-b" role="tablist">
                    {tab("account", "账户查询")}
                    {tab("recharge", "充值明细")}
                    {tab("consumption", "消费明细")}
                </div>
                {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="error" /> : null}
                {activeTab === "account" ? (
                    <>
                        <ZhTable
                            border
                            columns={
                                [
                                    {
                                        label: "账户编号",
                                        prop: "accountId",
                                        render: ({ row }) => row.account?.id || "--"
                                    },
                                    {
                                        label: "所属专区",
                                        prop: "zone",
                                        render: ({ row }) => row.specialZoneName || "--"
                                    },
                                    {
                                        label: "个人实名/用户ID",
                                        prop: "customer",
                                        render: ({ row }) => getCustomerLabel(row)
                                    },
                                    { label: "手机号", prop: "mobile", render: ({ row }) => row.mobile || "--" },
                                    {
                                        label: "机构名称",
                                        prop: "institution",
                                        render: ({ row }) => row.institutionName || "--"
                                    },
                                    {
                                        label: "专区余额（元)",
                                        prop: "balance",
                                        render: ({ row }) => Number(row.balanceAmount || 0).toFixed(2)
                                    },
                                    {
                                        label: "开户时间",
                                        prop: "account.createdTime",
                                        sortable: true,
                                        render: ({ row }) =>
                                            dateTimeFormat(row.account?.createdTime, "YYYY-MM-DD HH:mm")
                                    },
                                    {
                                        label: "操作",
                                        prop: "operation",
                                        render: ({ row }) =>
                                            canManage ? (
                                                <Button
                                                    aria-label="充值明细"
                                                    size="small"
                                                    onClick={() => {
                                                        setRechargeAccountId(row.customerCode);
                                                        setActiveTab("recharge");
                                                    }}
                                                >
                                                    充值明细
                                                </Button>
                                            ) : null
                                    }
                                ] as TableColumnProps<ZoneAccountRow>[]
                            }
                            data={rows}
                            emptyText="暂无账户"
                            pagination={false}
                            onSortChange={handleSortChange}
                            rowKey={row => String(row.account?.id || row.customerCode)}
                            isShowResetBtn={false}
                            isShowSearchBtn={false}
                            tableHeader={
                                <div className="admin-table-composed-header">
                                    <Space className="mb-4" wrap>
                                        <Input
                                            aria-label="账户编号"
                                            placeholder="请输入账户编号搜索"
                                            value={accountId}
                                            onChange={event => setAccountId(event.target.value)}
                                        />
                                        <Input
                                            aria-label="用户关键词"
                                            placeholder="请输入用户ID/个人或机构实名/手机号搜索"
                                            value={keyword}
                                            onChange={event => setKeyword(event.target.value)}
                                        />
                                        <label>
                                            开户日期
                                            <ZhDateRangePicker
                                                type="daterange"
                                                format="YYYY-MM-DD"
                                                valueFormat="YYYY-MM-DD"
                                                rangeSeparator="至"
                                                startPlaceholder="开户开始日期"
                                                endPlaceholder="开户结束日期"
                                                value={createdDateRange}
                                                onChange={(_value, formattedValue) =>
                                                    setCreatedDateRange(
                                                        formattedValue
                                                            ? [formattedValue[0] || null, formattedValue[1] || null]
                                                            : [null, null]
                                                    )
                                                }
                                            />
                                        </label>
                                        <Button aria-label="查询账户" type="primary" onClick={search}>
                                            查询
                                        </Button>
                                        {canManage ? (
                                            <Button
                                                aria-label="导出账户"
                                                loading={exporting}
                                                onClick={() => void handleExport()}
                                            >
                                                导出
                                            </Button>
                                        ) : null}
                                    </Space>
                                </div>
                            }
                            append={
                                <div className="admin-table-composed-pagination">
                                    <ZhPagination
                                        className="mt-4 self-end"
                                        current={query.page}
                                        pageSize={query.size}
                                        total={total}
                                        onChange={(page, size) => setQuery(current => ({ ...current, page, size }))}
                                    />
                                </div>
                            }
                        />
                    </>
                ) : activeTab === "recharge" ? (
                    <ZoneRechargePage
                        accountId={rechargeAccountId}
                        loadRecharges={loadRecharges}
                        permissions={currentPermissions}
                    />
                ) : (
                    <ZoneConsumptionPage loadConsumptions={loadConsumptions} permissions={currentPermissions} />
                )}
            </Card>
        </section>
    );
}

export default ZoneAccountPage;
